/**
 * The event vocabulary. `04 §3.3`, authoritative over the architecture's §8.2 list.
 *
 * SEVENTEEN CONTENT KINDS AND TWO CONTROL KINDS. The content kinds are persisted in the `events`
 * container and replayable; the control kinds describe the stream and are never stored.
 */

/** Kinds persisted in the event log and replayable. */
export const CONTENT_KINDS = [
  'state', 'run', 'phase', 'progress', 'token', 'tool', 'eligibility', 'spec', 'document',
  'flag', 'rate_limit', 'result', 'frozen', 'gate', 'error', 'halted', 'done',
] as const

/** Kinds that describe the stream itself. Never stored as content. */
export const CONTROL_KINDS = ['replay_incomplete', 'token_gap', 'recycle'] as const

export type ContentKind = (typeof CONTENT_KINDS)[number]
export type ControlKind = (typeof CONTROL_KINDS)[number]

/**
 * One frame.
 *
 * `kind` is deliberately a plain `string` rather than the union. AN UNKNOWN KIND IS IGNORED, NEVER
 * THROWN ON (`AC-SSE-7`) — that is what made adding `progress` a non-breaking change, and a type
 * that forbade unknown kinds would push the same breakage into the compiler instead.
 */
export interface Frame {
  kind: string
  seq?: number
  [field: string]: unknown
}

/** Whether this is a kind the client knows how to render. */
export function isKnown(kind: string): boolean {
  return (CONTENT_KINDS as readonly string[]).includes(kind)
    || (CONTROL_KINDS as readonly string[]).includes(kind)
}

/**
 * Parse one SSE wire frame.
 *
 * <p>THERE IS NO `event:` FIELD — every frame is the default `message` type, which is retained
 * deliberately and stated because a developer who assumes `addEventListener("phase", …)` writes a
 * handler that never fires.</p>
 *
 * Returns null for a comment (`: heartbeat`) or anything whose `data` is not an object, because a
 * heartbeat is not an event and a malformed frame must not become one.
 */
export function parseFrame(raw: string): Frame | null {
  let data: string | null = null
  let id: number | undefined

  for (const line of raw.split('\n')) {
    if (line.startsWith(':')) continue
    if (line.startsWith('data:')) data = (data === null ? '' : data + '\n') + line.slice(5).trim()
    else if (line.startsWith('id:')) {
      const n = Number.parseInt(line.slice(3).trim(), 10)
      if (Number.isFinite(n)) id = n
    }
  }

  if (data === null) return null

  try {
    const parsed: unknown = JSON.parse(data)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null
    const frame = parsed as Frame
    if (typeof frame.kind !== 'string') return null
    if (frame.seq === undefined && id !== undefined) frame.seq = id
    return frame
  } catch {
    // A frame that is not JSON is not an event. Dropping it is right; throwing would end the
    // stream for every later frame that was perfectly good.
    return null
  }
}

/**
 * Split a byte stream into SSE frames, yielding each as it completes.
 *
 * `EventSource` would do this, AND IT CANNOT SEND AN `Authorization` HEADER (A-4). A real access
 * token in a URL "would then appear in Application Gateway access logs, Application Insights
 * request telemetry and browser history", so the ~60 lines here are the price of keeping it in a
 * header where it belongs.
 */
export async function* parseSseFrames(body: ReadableStream<Uint8Array>): AsyncGenerator<Frame> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let split: number
      // A frame ends at a BLANK LINE. `\r\n\r\n` is accepted too: the spec permits it and a proxy
      // may rewrite line endings, and a client that only knew `\n\n` would hang on a stream that
      // was perfectly valid.
      while ((split = indexOfBoundary(buffer)) >= 0) {
        const raw = buffer.slice(0, split)
        buffer = buffer.slice(split).replace(/^(\r?\n){2}/, '')
        const frame = parseFrame(raw)
        if (frame) yield frame
      }
    }
  } finally {
    reader.releaseLock()
  }
}

function indexOfBoundary(buffer: string): number {
  const lf = buffer.indexOf('\n\n')
  const crlf = buffer.indexOf('\r\n\r\n')
  if (lf < 0) return crlf
  if (crlf < 0) return lf
  return Math.min(lf, crlf)
}
